import { ArrowLeft, Printer } from "lucide-react";
import { useMemo, useState, type CSSProperties } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { StatusBadge } from "../../components/StatusBadge";
import { formatNumber, formatSizeLabel } from "../../lib/format";
import { productRows } from "../../lib/selectors";
import type { AppData, ProductRow, SkuRow } from "../../lib/types";

type BarcodeLabelRow = {
  product: ProductRow;
  sku: SkuRow;
  barcode: string;
};

type LabelPreset = {
  key: string;
  name: string;
  vendor: string;
  purpose: string;
  widthMm: number;
  heightMm: number;
  columns: number;
  rows: number;
};

type LabelLayout = "apparel" | "compact" | "tag";
type BarcodeSizing = "auto" | "manual";

type LabelSettings = {
  layout: LabelLayout;
  barcodeSizing: BarcodeSizing;
  barcodeHeightMm: number;
  barcodeScale: number;
  fontSize: number;
  showProductName: boolean;
  showReadableAlias: boolean;
  showSku: boolean;
  showColorSize: boolean;
};

type PrintCell = BarcodeLabelRow | null;

const DEFAULT_PRESET_KEY = "formtec-3102";

const LABEL_PRESETS: LabelPreset[] = [
  { key: "formtec-3100", name: "Formtec 3100 바코드 소형", vendor: "Formtec", purpose: "소형 바코드/상품 식별", widthMm: 38.1, heightMm: 21.2, columns: 5, rows: 13 },
  { key: "formtec-3101", name: "Formtec 3101 바코드 초소형", vendor: "Formtec", purpose: "작은 상품 식별", widthMm: 38.1, heightMm: 19.2, columns: 5, rows: 12 },
  { key: "formtec-3102", name: "Formtec 3102 바코드용", vendor: "Formtec", purpose: "상품/의류 바코드 기본", widthMm: 47, heightMm: 26.9, columns: 4, rows: 10 },
  { key: "formtec-3105", name: "Formtec 3105 중형", vendor: "Formtec", purpose: "상품명 포함 라벨", widthMm: 63.5, heightMm: 38.1, columns: 3, rows: 7 },
  { key: "formtec-3106", name: "Formtec 3106 중형 낮은 라벨", vendor: "Formtec", purpose: "상품 식별/입출고 라벨", widthMm: 64, heightMm: 34, columns: 3, rows: 8 },
  { key: "formtec-3107", name: "Formtec 3107 와이드", vendor: "Formtec", purpose: "행택/박스 식별", widthMm: 99.1, heightMm: 33.9, columns: 2, rows: 8 },
  { key: "formtec-3108", name: "Formtec 3108 와이드", vendor: "Formtec", purpose: "행택/상품 정보 여유", widthMm: 99.1, heightMm: 38.1, columns: 2, rows: 7 },
  { key: "formtec-3109", name: "Formtec 3109 와이드 낮은 라벨", vendor: "Formtec", purpose: "박스/상품 식별", widthMm: 100, heightMm: 30, columns: 2, rows: 9 },
  { key: "formtec-3112", name: "Formtec 3112 물류 라벨", vendor: "Formtec", purpose: "큰 상품/물류 식별", widthMm: 63.5, heightMm: 70, columns: 3, rows: 4 },
  { key: "formtec-3114", name: "Formtec 3114 대형 물류", vendor: "Formtec", purpose: "박스/보관 위치 식별", widthMm: 99.1, heightMm: 67.7, columns: 2, rows: 4 },
  { key: "formtec-3212", name: "Formtec 3212 세로 여유", vendor: "Formtec", purpose: "상품명/옵션 정보 넉넉한 라벨", widthMm: 100, heightMm: 45, columns: 2, rows: 6 },
  { key: "formtec-3218", name: "Formtec 3218 중형 세로 여유", vendor: "Formtec", purpose: "의류 옵션/상품명 포함", widthMm: 63.5, heightMm: 45, columns: 3, rows: 6 },
  { key: "formtec-3219", name: "Formtec 3219 슬림 와이드", vendor: "Formtec", purpose: "행택 보조/선반 라벨", widthMm: 100, heightMm: 24.5, columns: 2, rows: 11 },
  { key: "formtec-3620", name: "Formtec 3620 대형 바코드", vendor: "Formtec", purpose: "큰 바코드/박스 식별", widthMm: 119.3, heightMm: 42.8, columns: 1, rows: 6 },
  { key: "formtec-3667", name: "Formtec 3667 물류 중형", vendor: "Formtec", purpose: "입출고/재고 위치 라벨", widthMm: 76, heightMm: 44.4, columns: 2, rows: 6 },
  { key: "printec-v3410", name: "Printec V3410 바코드용", vendor: "Printec", purpose: "상품/의류 바코드 기본", widthMm: 47, heightMm: 26.9, columns: 4, rows: 10 },
  { key: "printec-a0210", name: "Printec A0210 중형", vendor: "Printec", purpose: "상품명 포함 라벨", widthMm: 63.5, heightMm: 38.1, columns: 3, rows: 7 },
  { key: "avery-4780", name: "Avery Zweckform 4780", vendor: "Avery", purpose: "범용 바코드 라벨", widthMm: 48.5, heightMm: 25.4, columns: 4, rows: 10 },
  { key: "avery-3475", name: "Avery Zweckform 3475", vendor: "Avery", purpose: "범용 상품/문서 식별", widthMm: 70, heightMm: 36, columns: 3, rows: 8 },
  { key: "avery-3474", name: "Avery Zweckform 3474", vendor: "Avery", purpose: "상품/문서 식별", widthMm: 70, heightMm: 37, columns: 3, rows: 8 },
  { key: "avery-3652", name: "Avery Zweckform 3652", vendor: "Avery", purpose: "상품명 포함 라벨", widthMm: 70, heightMm: 42.3, columns: 3, rows: 7 },
  { key: "avery-3659", name: "Avery Zweckform 3659", vendor: "Avery", purpose: "행택/상품명 라벨", widthMm: 97, heightMm: 42.3, columns: 2, rows: 6 },
  { key: "avery-3665", name: "Avery Zweckform 3665", vendor: "Avery", purpose: "상품 넘버링/와이드 바코드", widthMm: 105, heightMm: 33.8, columns: 2, rows: 8 },
  { key: "avery-3678", name: "Avery Zweckform 3678", vendor: "Avery", purpose: "보관함/상품 식별", widthMm: 97, heightMm: 37, columns: 2, rows: 7 },
  { key: "custom", name: "직접 입력", vendor: "Custom", purpose: "보유 라벨지에 맞춤", widthMm: 50, heightMm: 30, columns: 4, rows: 10 }
];

const EAN_L: Record<string, string> = {
  "0": "0001101",
  "1": "0011001",
  "2": "0010011",
  "3": "0111101",
  "4": "0100011",
  "5": "0110001",
  "6": "0101111",
  "7": "0111011",
  "8": "0110111",
  "9": "0001011"
};

const EAN_G: Record<string, string> = {
  "0": "0100111",
  "1": "0110011",
  "2": "0011011",
  "3": "0100001",
  "4": "0011101",
  "5": "0111001",
  "6": "0000101",
  "7": "0010001",
  "8": "0001001",
  "9": "0010111"
};

const EAN_R: Record<string, string> = {
  "0": "1110010",
  "1": "1100110",
  "2": "1101100",
  "3": "1000010",
  "4": "1011100",
  "5": "1001110",
  "6": "1010000",
  "7": "1000100",
  "8": "1001000",
  "9": "1110100"
};

const EAN_PARITY = [
  "LLLLLL",
  "LLGLGG",
  "LLGGLG",
  "LLGGGL",
  "LGLLGG",
  "LGGLLG",
  "LGGGLL",
  "LGLGLG",
  "LGLGGL",
  "LGGLGL"
];

export function BarcodeLabelsPage({ data }: { data: AppData }) {
  const [params] = useSearchParams();
  const productKey = params.get("product") || "";
  const skuKey = params.get("sku") || "";
  const [presetKey, setPresetKey] = useState(DEFAULT_PRESET_KEY);
  const [customLabelWidthMm, setCustomLabelWidthMm] = useState(50);
  const [customLabelHeightMm, setCustomLabelHeightMm] = useState(30);
  const [customColumns, setCustomColumns] = useState(4);
  const [customRowsPerSheet, setCustomRowsPerSheet] = useState(10);
  const [copiesPerSku, setCopiesPerSku] = useState(() => skuKey ? presetCapacity(DEFAULT_PRESET_KEY) : 1);
  const [startCell, setStartCell] = useState(1);
  const [offsetXmm, setOffsetXmm] = useState(0);
  const [offsetYmm, setOffsetYmm] = useState(0);
  const [settings, setSettings] = useState<LabelSettings>({
    layout: "apparel",
    barcodeSizing: "auto",
    barcodeHeightMm: 12,
    barcodeScale: 96,
    fontSize: 10,
    showProductName: true,
    showReadableAlias: false,
    showSku: true,
    showColorSize: true
  });
  const labels = barcodeLabelRows(data, productKey, skuKey);
  const title = skuKey ? "SKU 바코드 라벨" : "상품 바코드 라벨";
  const activePreset = LABEL_PRESETS.find((preset) => preset.key === presetKey) || LABEL_PRESETS[0];
  const isCustomPreset = activePreset.key === "custom";
  const labelWidthMm = isCustomPreset ? customLabelWidthMm : activePreset.widthMm;
  const labelHeightMm = isCustomPreset ? customLabelHeightMm : activePreset.heightMm;
  const columns = isCustomPreset ? customColumns : activePreset.columns;
  const rowsPerSheet = isCustomPreset ? customRowsPerSheet : activePreset.rows;
  const sheetCount = Math.max(1, columns * rowsPerSheet);
  const safeStartCell = Math.min(Math.max(1, startCell), sheetCount);
  const printLabels = useMemo(() => expandPrintLabels(labels, copiesPerSku), [copiesPerSku, labels]);
  const printSheets = useMemo(() => buildPrintSheets(printLabels, safeStartCell, sheetCount), [printLabels, safeStartCell, sheetCount]);
  const sheetLayout = useMemo(() => labelSheetLayout(labelWidthMm, labelHeightMm, columns, rowsPerSheet, offsetXmm, offsetYmm), [columns, labelHeightMm, labelWidthMm, offsetXmm, offsetYmm, rowsPerSheet]);
  const visibleTextLines = visibleLabelLineCount(settings);
  const autoBarcodeHeightMm = autoBarcodeHeight(labelHeightMm, settings.fontSize, visibleTextLines, settings.layout);
  const autoBarcodeScale = autoBarcodeScalePercent(labelWidthMm, visibleTextLines, settings.layout);
  const resolvedBarcodeHeightMm = settings.barcodeSizing === "auto" ? autoBarcodeHeightMm : settings.barcodeHeightMm;
  const resolvedBarcodeScale = settings.barcodeSizing === "auto" ? autoBarcodeScale : settings.barcodeScale;
  const pageStyle = useMemo(() => ({
    "--label-width": `${labelWidthMm}mm`,
    "--label-height": `${labelHeightMm}mm`,
    "--label-columns": String(columns),
    "--label-rows": String(rowsPerSheet),
    "--sheet-width": `${sheetLayout.widthMm}mm`,
    "--sheet-height": `${sheetLayout.heightMm}mm`,
    "--sheet-margin-left": `${sheetLayout.marginLeftMm}mm`,
    "--sheet-margin-top": `${sheetLayout.marginTopMm}mm`,
    "--sheet-margin-right": `${sheetLayout.marginRightMm}mm`,
    "--sheet-margin-bottom": `${sheetLayout.marginBottomMm}mm`,
    "--sheet-offset-x": `${offsetXmm}mm`,
    "--sheet-offset-y": `${offsetYmm}mm`,
    "--barcode-height": `${resolvedBarcodeHeightMm}mm`,
    "--label-font-size": `${settings.fontSize}px`
  }) as CSSProperties, [columns, labelHeightMm, labelWidthMm, offsetXmm, offsetYmm, resolvedBarcodeHeightMm, rowsPerSheet, settings.fontSize, sheetLayout.heightMm, sheetLayout.marginBottomMm, sheetLayout.marginLeftMm, sheetLayout.marginRightMm, sheetLayout.marginTopMm, sheetLayout.widthMm]);

  function applyPreset(nextKey: string) {
    const preset = LABEL_PRESETS.find((item) => item.key === nextKey) || LABEL_PRESETS[0];
    const currentSheetCount = sheetCount;
    setPresetKey(nextKey);
    if (preset.key === "custom") {
      setCustomLabelWidthMm(preset.widthMm);
      setCustomLabelHeightMm(preset.heightMm);
      setCustomColumns(preset.columns);
      setCustomRowsPerSheet(preset.rows);
    }
    setCopiesPerSku((current) => skuKey && current === currentSheetCount ? preset.columns * preset.rows : current);
    setStartCell(1);
    setOffsetXmm(0);
    setOffsetYmm(0);
  }

  function applyLayout(nextLayout: LabelLayout) {
    setSettings((current) => ({
      ...current,
      layout: nextLayout,
      barcodeSizing: "auto",
      barcodeHeightMm: nextLayout === "compact" ? 14 : nextLayout === "tag" ? 13 : 12,
      barcodeScale: nextLayout === "compact" ? 98 : 96,
      showProductName: nextLayout !== "compact",
      showReadableAlias: nextLayout === "tag",
      showSku: true,
      showColorSize: nextLayout !== "compact"
    }));
  }

  return (
    <div className="page-stack barcode-label-page" style={pageStyle}>
      <section className="toolbar-panel print-hidden">
        <div className="toolbar-row">
          <Link className="text-button" to="/products">
            <ArrowLeft size={15} />
            Products
          </Link>
          <button type="button" className="primary" onClick={() => window.print()} disabled={!labels.length}>
            <Printer size={15} />
            라벨 출력
          </button>
        </div>
        <div className="label-config-grid">
          <label>
            라벨지
            <select value={presetKey} onChange={(event) => applyPreset(event.target.value)}>
              {LABEL_PRESETS.map((preset) => (
                <option key={preset.key} value={preset.key}>{preset.name}</option>
              ))}
            </select>
          </label>
          <label>
            형태
            <select value={settings.layout} onChange={(event) => applyLayout(event.target.value as LabelLayout)}>
              <option value="apparel">의류 상품 라벨</option>
              <option value="compact">소형 바코드 중심</option>
              <option value="tag">행택 보조 라벨</option>
            </select>
          </label>
          <label>
            SKU당 수량
            <input type="number" min={1} max={999} value={copiesPerSku} onChange={(event) => setCopiesPerSku(Math.max(1, Number(event.target.value || 1)))} />
          </label>
          <label>
            시작 칸
            <input type="number" min={1} max={sheetCount} value={safeStartCell} onChange={(event) => setStartCell(Math.max(1, Number(event.target.value || 1)))} />
          </label>
          <label>
            좌우 보정 mm
            <input type="number" min={-20} max={20} step={0.1} value={offsetXmm} onChange={(event) => setOffsetXmm(Number(event.target.value || 0))} />
          </label>
          <label>
            상하 보정 mm
            <input type="number" min={-20} max={20} step={0.1} value={offsetYmm} onChange={(event) => setOffsetYmm(Number(event.target.value || 0))} />
          </label>
          <label>
            바코드 크기
            <select value={settings.barcodeSizing} onChange={(event) => setSettings((current) => ({ ...current, barcodeSizing: event.target.value as BarcodeSizing }))}>
              <option value="auto">자동</option>
              <option value="manual">수동</option>
            </select>
          </label>
          <label>
            높이 mm
            <input type="number" min={6} max={40} step={0.5} value={settings.barcodeSizing === "auto" ? roundHalf(resolvedBarcodeHeightMm) : settings.barcodeHeightMm} disabled={settings.barcodeSizing === "auto"} onChange={(event) => setSettings((current) => ({ ...current, barcodeHeightMm: Number(event.target.value || 0) }))} />
          </label>
          <label>
            폭 % (여백 포함)
            <input type="number" min={60} max={100} value={settings.barcodeSizing === "auto" ? resolvedBarcodeScale : settings.barcodeScale} disabled={settings.barcodeSizing === "auto"} onChange={(event) => setSettings((current) => ({ ...current, barcodeScale: Number(event.target.value || 0) }))} />
          </label>
          <label>
            텍스트 px
            <input type="number" min={8} max={14} value={settings.fontSize} onChange={(event) => setSettings((current) => ({ ...current, fontSize: Number(event.target.value || 0) }))} />
          </label>
        </div>
        <div className="label-spec-grid" aria-label="선택한 라벨지 규격">
          <div><span>라벨</span><strong>{labelWidthMm} x {labelHeightMm}mm</strong></div>
          <div><span>배열</span><strong>{columns}열 x {rowsPerSheet}행</strong></div>
          <div><span>용지</span><strong>A4 · {formatNumber(sheetCount)}칸</strong></div>
          <div><span>인쇄</span><strong>시작 {safeStartCell}번 · {formatNumber(printLabels.length)}개</strong></div>
        </div>
        {isCustomPreset ? (
          <div className="label-custom-grid" aria-label="직접 입력 라벨지 규격">
            <label>
              가로 mm
              <input type="number" min={20} step={0.1} value={customLabelWidthMm} onChange={(event) => setCustomLabelWidthMm(Number(event.target.value || 0))} />
            </label>
            <label>
              세로 mm
              <input type="number" min={10} step={0.1} value={customLabelHeightMm} onChange={(event) => setCustomLabelHeightMm(Number(event.target.value || 0))} />
            </label>
            <label>
              열
              <input type="number" min={1} max={8} value={customColumns} onChange={(event) => setCustomColumns(Math.max(1, Number(event.target.value || 1)))} />
            </label>
            <label>
              행
              <input type="number" min={1} max={20} value={customRowsPerSheet} onChange={(event) => setCustomRowsPerSheet(Math.max(1, Number(event.target.value || 1)))} />
            </label>
          </div>
        ) : null}
        <div className="label-toggle-row">
          <label><input type="checkbox" checked={settings.showProductName} onChange={(event) => setSettings((current) => ({ ...current, showProductName: event.target.checked }))} />상품명</label>
          <label><input type="checkbox" checked={settings.showReadableAlias} onChange={(event) => setSettings((current) => ({ ...current, showReadableAlias: event.target.checked }))} />Readable alias</label>
          <label><input type="checkbox" checked={settings.showSku} onChange={(event) => setSettings((current) => ({ ...current, showSku: event.target.checked }))} />SKU</label>
          <label><input type="checkbox" checked={settings.showColorSize} onChange={(event) => setSettings((current) => ({ ...current, showColorSize: event.target.checked }))} />컬러/사이즈</label>
        </div>
      </section>

      <section className="panel barcode-label-summary">
        <div className="panel-head">
          <div>
            <h2>{title}</h2>
            <p>{activePreset.vendor} · {labelWidthMm} x {labelHeightMm}mm · {columns}열 x {rowsPerSheet}행 · SKU당 {copiesPerSku}개 · 시작 {safeStartCell}번 · {activePreset.purpose}</p>
          </div>
          <StatusBadge tone={labels.length ? "success" : "warning"}>{formatNumber(labels.length)} SKU · {formatNumber(printLabels.length)}개 출력 · {formatNumber(printSheets.length || 0)}장</StatusBadge>
        </div>
      </section>

      {labels.length ? (
        <section className="barcode-label-grid barcode-label-preview" aria-label="바코드 라벨 대표 미리보기">
          {labels.slice(0, 1).map((label) => (
            <BarcodeLabel key={`${label.product.productKey}-${String(label.sku.sku || label.sku.readableAlias)}`} label={label} settings={settings} barcodeScale={resolvedBarcodeScale} />
          ))}
        </section>
      ) : (
        <section className="panel empty-state">
          <h2>출력할 바코드가 없습니다.</h2>
          <p>상품의 SKU 매트릭스에서 발급된 바코드를 선택해 주세요.</p>
        </section>
      )}

      {printSheets.length ? (
        <section className="barcode-print-sheets" aria-label="인쇄용 바코드 라벨">
          {printSheets.map((sheet, sheetIndex) => (
            <div className="barcode-print-sheet" key={`sheet-${sheetIndex + 1}`}>
              {sheet.map((cell, cellIndex) => cell ? (
                <div className="barcode-print-cell" key={`${sheetIndex}-${cellIndex}-${cell.sku.sku || cell.barcode}`}>
                  <BarcodeLabel label={cell} settings={settings} barcodeScale={resolvedBarcodeScale} />
                </div>
              ) : (
                <div className="barcode-print-cell barcode-print-cell--blank" key={`${sheetIndex}-${cellIndex}-blank`} aria-hidden="true" />
              ))}
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
}

function BarcodeLabel({ label, settings, barcodeScale }: { label: BarcodeLabelRow; settings: LabelSettings; barcodeScale: number }) {
  const skuCode = String(label.sku.sku || "-");
  const alias = String(label.sku.readableAlias || "-");
  const colorSize = `${label.sku.colorwayName || label.sku.colorwayCode || "-"} / ${formatSizeLabel(label.sku.sizeName || label.sku.sizeCode)}`;
  const isEan13 = /^\d{13}$/.test(label.barcode);
  const checksumOk = isEan13 ? ean13Checksum(label.barcode.slice(0, 12)) === label.barcode[12] : false;

  return (
    <article className={`barcode-label-card barcode-label-card--${settings.layout}`}>
      {(settings.showProductName || settings.showReadableAlias) ? (
        <div className="barcode-label-meta">
          {settings.showProductName ? <strong>{label.product.productName}</strong> : null}
          {settings.showReadableAlias ? <span>{alias}</span> : null}
        </div>
      ) : null}
      {isEan13 ? <Ean13Svg value={label.barcode} scale={barcodeScale} /> : <div className="barcode-label-fallback">{label.barcode}</div>}
      <div className="barcode-label-foot">
        {settings.showSku ? <span>{skuCode}</span> : null}
        {settings.showColorSize ? <span>{colorSize}</span> : null}
      </div>
      {isEan13 && !checksumOk ? <p className="barcode-label-warning">EAN-13 체크섬 확인 필요</p> : null}
    </article>
  );
}

function Ean13Svg({ value, scale }: { value: string; scale: number }) {
  const modules = ean13Modules(value);
  const quietLeftModules = 11;
  const quietRightModules = 7;
  const barWidth = 2;
  const width = (modules.length + quietLeftModules + quietRightModules) * barWidth;
  return (
    <svg viewBox={`0 0 ${width} 86`} role="img" aria-label={`EAN-13 ${value}`} style={{ width: `${scale}%` }}>
      <rect x="0" y="0" width={width} height="86" fill="#fff" />
      {modules.split("").map((bit, index) => bit === "1" ? (
        <rect key={index} x={(index + quietLeftModules) * barWidth} y="4" width={barWidth} height="62" fill="#111" />
      ) : null)}
      <text x={width / 2} y="82" textAnchor="middle" fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" fontSize="12" fontWeight="700">
        {value}
      </text>
    </svg>
  );
}

function barcodeLabelRows(data: AppData, productKey: string, skuKey: string): BarcodeLabelRow[] {
  const products = productRows(data);
  return products.flatMap((product) => {
    if (productKey && product.productKey !== productKey) return [];
    return product.rows.flatMap((sku) => {
      if (skuKey && String(sku.sku || "") !== skuKey) return [];
      const barcode = barcodeValue(sku);
      return barcode ? [{ product, sku, barcode }] : [];
    });
  });
}

function barcodeValue(row: SkuRow): string {
  return String(row.barcode || row.gtin || row.ean13 || row.ean || "").trim();
}

function presetCapacity(presetKey: string): number {
  const preset = LABEL_PRESETS.find((item) => item.key === presetKey) || LABEL_PRESETS[0];
  return Math.max(1, preset.columns * preset.rows);
}

function expandPrintLabels(labels: BarcodeLabelRow[], copiesPerSku: number): BarcodeLabelRow[] {
  const copyCount = Math.max(1, Math.floor(copiesPerSku || 1));
  return labels.flatMap((label) => Array.from({ length: copyCount }, () => label));
}

function buildPrintSheets(labels: BarcodeLabelRow[], startCell: number, sheetCapacity: number): PrintCell[][] {
  if (!labels.length || sheetCapacity < 1) return [];
  const leadingBlanks = Math.min(Math.max(0, startCell - 1), sheetCapacity - 1);
  const cells: PrintCell[] = [...Array.from({ length: leadingBlanks }, () => null), ...labels];
  const sheetTotal = Math.ceil(cells.length / sheetCapacity);

  return Array.from({ length: sheetTotal }, (_, sheetIndex) => {
    const start = sheetIndex * sheetCapacity;
    const sheet = cells.slice(start, start + sheetCapacity);
    return [...sheet, ...Array.from({ length: Math.max(0, sheetCapacity - sheet.length) }, () => null)];
  });
}

function labelSheetLayout(labelWidthMm: number, labelHeightMm: number, columns: number, rows: number, offsetXmm: number, offsetYmm: number) {
  const widthMm = 210;
  const heightMm = 297;
  const gridWidthMm = Math.max(0, labelWidthMm * columns);
  const gridHeightMm = Math.max(0, labelHeightMm * rows);
  const baseMarginLeftMm = Math.max(0, (widthMm - gridWidthMm) / 2);
  const baseMarginTopMm = Math.max(0, (heightMm - gridHeightMm) / 2);
  const marginLeftMm = Math.max(0, baseMarginLeftMm + offsetXmm);
  const marginTopMm = Math.max(0, baseMarginTopMm + offsetYmm);
  const marginRightMm = Math.max(0, widthMm - gridWidthMm - marginLeftMm);
  const marginBottomMm = Math.max(0, heightMm - gridHeightMm - marginTopMm);

  return {
    widthMm,
    heightMm,
    marginLeftMm: roundTenth(marginLeftMm),
    marginTopMm: roundTenth(marginTopMm),
    marginRightMm: roundTenth(marginRightMm),
    marginBottomMm: roundTenth(marginBottomMm)
  };
}

function ean13Modules(value: string): string {
  const digits = value.split("");
  const first = Number(digits[0] || 0);
  const parity = EAN_PARITY[first] || EAN_PARITY[0];
  const left = digits.slice(1, 7).map((digit, index) => parity[index] === "G" ? EAN_G[digit] : EAN_L[digit]).join("");
  const right = digits.slice(7).map((digit) => EAN_R[digit]).join("");
  return `101${left}01010${right}101`;
}

function ean13Checksum(twelveDigits: string): string {
  const sum = twelveDigits.split("").reduce((total, digit, index) => {
    const value = Number(digit || 0);
    return total + value * (index % 2 === 0 ? 1 : 3);
  }, 0);
  return String((10 - (sum % 10)) % 10);
}

function visibleLabelLineCount(settings: LabelSettings): number {
  let count = 0;
  if (settings.showProductName) count += 1;
  if (settings.showReadableAlias) count += 1;
  if (settings.showSku || settings.showColorSize) count += 1;
  return count;
}

function autoBarcodeHeight(labelHeightMm: number, fontSize: number, textLines: number, layout: LabelLayout): number {
  const textLineMm = Math.max(2.7, fontSize * 0.31);
  const paddingMm = 4;
  const gapMm = textLines > 0 ? 1.3 : 0.8;
  const reservedMm = textLines * textLineMm + paddingMm + gapMm;
  const maxRatio = layout === "compact" ? 0.76 : layout === "tag" ? 0.62 : 0.58;
  const available = Math.max(7, labelHeightMm - reservedMm);
  return roundHalf(Math.max(7, Math.min(available, labelHeightMm * maxRatio)));
}

function autoBarcodeScalePercent(labelWidthMm: number, textLines: number, layout: LabelLayout): number {
  return 100;
}

function roundHalf(value: number): number {
  return Math.round(value * 2) / 2;
}

function roundTenth(value: number): number {
  return Math.round(value * 10) / 10;
}
